import {
  Component,
  State,
  Element,
  Listen,
  Method,
  Prop,
  EventEmitter,
  Event,
  h,
  Host,
} from "@stencil/core";

const UNFOCUSED_INDEX = -1;
const SUPPORTED_OPTIONS = [
  "scs-autocomplete-option",
  "scs-autocomplete-product-option",
  "scs-autocomplete-quick-link-option",
];

@Component({
  tag: "scs-autocomplete",
  styleUrls: ["autocomplete.scss"],
  shadow: true,
})
export class Autocomplete {
  private focusIndex: number = UNFOCUSED_INDEX;
  private autocompleteOptions: Array<
    | HTMLScsAutocompleteOptionElement
    | HTMLScsAutocompleteProductOptionElement
    | HTMLScsAutocompleteQuickLinkOptionElement
  > = [] as any;

  @Element() host!: HTMLScsAutocompleteElement;

  @Prop() inputReferenceId!: string;

  @State() openState: boolean = true;

  @Event() autocompleteVisibilityChanged!: EventEmitter<boolean>;
  @Event() autocompleteFocusChanged!: EventEmitter<number>;

  @Listen("keydown", { target: "window" })
  keydownHandler(ev: KeyboardEvent) {
    if (!this.openState) return;

    switch (ev.key) {
      case "ArrowDown":
        ev.preventDefault();
        this.focusNext();
        break;
      case "ArrowUp":
        ev.preventDefault();
        this.focusPrev();
        break;
    }
  }

  @Listen("autocompleteOptionLoaded")
  autocompleteOptionLoadedHandler() {
    this.autocompleteOptions = this.queryOptions();
    this.open();
  }

  /**
   * Should be only used by autocomplete-option element to remove option when component
   * did unload
   */
  @Method()
  async autocompleteOptionRemoved() {
    this.autocompleteOptions = this.queryOptions();

    if (this.autocompleteOptions.length < this.focusIndex + 1) {
      this.resetFocus();
    }
  }

  private queryOptions(): Array<
    HTMLScsAutocompleteOptionElement | HTMLScsAutocompleteQuickLinkOptionElement
  > {
    const options: HTMLScsAutocompleteQuickLinkOptionElement[] = [];
    SUPPORTED_OPTIONS.forEach((tag: string) =>
      this.host
        .querySelectorAll(tag)
        .forEach(element =>
          options.push(element as HTMLScsAutocompleteQuickLinkOptionElement),
        ),
    );

    return options;
  }

  private open() {
    if (this.autocompleteOptions.length) {
      this.openState = true;
      this.autocompleteVisibilityChanged.emit(true);
    }
  }

  private updateAutocompleteOptionFocus() {
    this.autocompleteOptions.forEach(
      (node: HTMLScsAutocompleteOptionElement, index: number) => {
        node.hasFocus = index === this.focusIndex;

        index++;
      },
    );
  }

  private focusNext() {
    const isLastOptionFocused =
      this.autocompleteOptions.length - 1 === this.focusIndex;
    this.setFocusIndex(
      isLastOptionFocused ? UNFOCUSED_INDEX : this.focusIndex + 1,
    );
  }

  private focusPrev() {
    const isUnfocused = this.focusIndex === UNFOCUSED_INDEX;
    // if no autocomplete option is focused set last option as focused
    this.setFocusIndex(
      isUnfocused ? this.autocompleteOptions.length - 1 : this.focusIndex - 1,
    );
  }

  private setFocusIndex(index: number) {
    if (this.focusIndex !== index) {
      this.focusIndex = index;
      this.updateAutocompleteOptionFocus();
      this.autocompleteFocusChanged.emit(index);
    }
  }

  private resetFocus() {
    this.setFocusIndex(UNFOCUSED_INDEX);
  }

  componentDidLoad() {
    const inputReference = this.host.parentElement!.querySelector(
      `#${this.inputReferenceId}`,
    ) as HTMLElement;

    if (inputReference) {
      inputReference.addEventListener("click", ev => {
        // ignores clicks on input field
        if (this.openState) {
          ev.stopPropagation();
        }
        this.open();
      });
    }
  }

  render() {
    return (
      <Host
        onClick={(event: MouseEvent) => event.stopPropagation()}
        role="listbox"
        class={{
          open: this.openState,
        }}
      >
        <slot />
      </Host>
    );
  }
}
